import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, X, Calendar, MapPin, Monitor, Users, Layers,
  CheckCircle2, Circle, Trash2, Pencil, CalendarRange,
  Search, Activity,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { BRAND_COLORS, NEUTRAL_COLORS, SYSTEM_COLORS } from '../../../constants/brandPalette';
import { eventsService, EVENT_STATUSES, EVENT_TYPES, EVENT_MODES } from '../api/eventsApi';

// ── Meta maps ─────────────────────────────────────────────────────────────────

const STATUS_META = {
  planning:  { label: 'Planning',  bg: SYSTEM_COLORS.warningTint,  color: SYSTEM_COLORS.warning  },
  scheduled: { label: 'Scheduled', bg: SYSTEM_COLORS.infoTint,     color: SYSTEM_COLORS.info     },
  ongoing:   { label: 'Ongoing',   bg: SYSTEM_COLORS.successTint,  color: SYSTEM_COLORS.success  },
  completed: { label: 'Completed', bg: SYSTEM_COLORS.surfaceMuted, color: SYSTEM_COLORS.textMuted },
  cancelled: { label: 'Cancelled', bg: SYSTEM_COLORS.dangerTint,   color: SYSTEM_COLORS.danger   },
};

const TYPE_META = {
  outreach:     { label: 'Outreach',     color: '#7c3aed', bg: '#ede9fe' },
  recruitment:  { label: 'Recruitment',  color: '#0369a1', bg: '#e0f2fe' },
  training:     { label: 'Training',     color: '#047857', bg: '#d1fae5' },
  ideation:     { label: 'Ideation',     color: '#b45309', bg: '#fef3c7' },
  engagement:   { label: 'Engagement',   color: '#be185d', bg: '#fce7f3' },
  speaker_talk: { label: 'Speaker Talk', color: '#1d4ed8', bg: '#dbeafe' },
  hackathon:    { label: 'Hackathon',    color: '#c2410c', bg: '#ffedd5' },
  conference:   { label: 'Conference',   color: '#374151', bg: '#f3f4f6' },
};

const MODE_META = {
  in_person: { label: 'In Person', icon: MapPin  },
  virtual:   { label: 'Virtual',   icon: Monitor },
  hybrid:    { label: 'Hybrid',    icon: Layers  },
};

// ── Shared input style ────────────────────────────────────────────────────────

const inputStyle = {
  borderColor: SYSTEM_COLORS.borderSoft,
  backgroundColor: SYSTEM_COLORS.shellSurface,
  color: NEUTRAL_COLORS.black,
};

const InputField = ({ label, required, children }) => (
  <div>
    <label className="block text-xs font-semibold mb-1.5" style={{ color: SYSTEM_COLORS.textMuted }}>
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

// ── ChecklistPanel ────────────────────────────────────────────────────────────

const ChecklistPanel = ({ eventId, items = [], onRefresh }) => {
  const [newItem, setNewItem] = useState('');
  const [newDue,  setNewDue]  = useState('');
  const [saving,  setSaving]  = useState(false);

  const handleAdd = async () => {
    if (!newItem.trim()) return;
    setSaving(true);
    try {
      await eventsService.addChecklistItem(eventId, { item: newItem.trim(), dueAt: newDue || undefined });
      setNewItem('');
      setNewDue('');
      onRefresh();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const handleToggle = async (item) => {
    try {
      await eventsService.updateChecklistItem(eventId, item.id, { done: !item.done });
      onRefresh();
    } catch (err) { toast.error(err.message); }
  };

  const handleDelete = async (itemId) => {
    try {
      await eventsService.deleteChecklistItem(eventId, itemId);
      onRefresh();
    } catch (err) { toast.error(err.message); }
  };

  const doneCount = items.filter(i => i.done).length;

  return (
    <div className="space-y-4">
      {items.length > 0 && (
        <div>
          <div className="flex justify-between text-xs mb-1.5" style={{ color: SYSTEM_COLORS.textMuted }}>
            <span>{doneCount} of {items.length} done</span>
            <span>{Math.round((doneCount / items.length) * 100)}%</span>
          </div>
          <div className="h-2 rounded-full" style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }}>
            <div className="h-2 rounded-full transition-all"
              style={{ width: `${(doneCount / items.length) * 100}%`, backgroundColor: SYSTEM_COLORS.success }} />
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <input
          type="text"
          placeholder="Add checklist item…"
          value={newItem}
          onChange={e => setNewItem(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleAdd()}
          className="flex-1 rounded-xl border px-3 py-2 text-sm outline-none"
          style={inputStyle}
        />
        <input
          type="date"
          value={newDue}
          onChange={e => setNewDue(e.target.value)}
          className="rounded-xl border px-2 py-2 text-sm outline-none"
          style={inputStyle}
        />
        <button
          onClick={handleAdd}
          disabled={saving || !newItem.trim()}
          className="flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold text-white disabled:opacity-50 flex-shrink-0"
          style={{ backgroundColor: BRAND_COLORS.primary }}
        >
          <Plus size={14} /> Add
        </button>
      </div>

      <div className="space-y-2">
        {items.length === 0 && (
          <p className="text-sm text-center py-6" style={{ color: SYSTEM_COLORS.textMuted }}>No checklist items yet</p>
        )}
        {items.map(item => (
          <div key={item.id}
            className="flex items-start gap-3 rounded-xl p-3 border"
            style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}
          >
            <button onClick={() => handleToggle(item)} className="flex-shrink-0 mt-0.5">
              {item.done
                ? <CheckCircle2 size={16} style={{ color: SYSTEM_COLORS.success }} />
                : <Circle size={16} style={{ color: SYSTEM_COLORS.textSoft }} />}
            </button>
            <div className="flex-1 min-w-0">
              <p className="text-sm" style={{
                color: NEUTRAL_COLORS.black,
                textDecoration: item.done ? 'line-through' : 'none',
                opacity: item.done ? 0.6 : 1,
              }}>
                {item.item}
              </p>
              {item.due_at && (
                <p className="text-xs mt-0.5" style={{ color: SYSTEM_COLORS.textMuted }}>
                  Due {new Date(item.due_at).toLocaleDateString()}
                </p>
              )}
            </div>
            <button
              onClick={() => handleDelete(item.id)}
              className="flex-shrink-0 p-1 rounded transition-colors hover:bg-red-50"
              style={{ color: SYSTEM_COLORS.textSoft }}
            >
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── NotesPanel ────────────────────────────────────────────────────────────────

const NotesPanel = ({ eventId, notes = [], onRefresh }) => {
  const [body,   setBody]   = useState('');
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    if (!body.trim()) return;
    setSaving(true);
    try {
      await eventsService.addNote(eventId, { body: body.trim() });
      setBody('');
      onRefresh();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (noteId) => {
    try {
      await eventsService.deleteNote(eventId, noteId);
      onRefresh();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <textarea
          rows={3}
          placeholder="Add a running note…"
          value={body}
          onChange={e => setBody(e.target.value)}
          className="w-full rounded-xl border px-3 py-2 text-sm outline-none resize-none"
          style={inputStyle}
        />
        <button
          onClick={handleAdd}
          disabled={saving || !body.trim()}
          className="flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          style={{ backgroundColor: BRAND_COLORS.primary }}
        >
          <Plus size={14} /> Add Note
        </button>
      </div>

      <div className="space-y-3">
        {notes.length === 0 && (
          <p className="text-sm text-center py-6" style={{ color: SYSTEM_COLORS.textMuted }}>No notes yet</p>
        )}
        {[...notes].reverse().map(note => (
          <div key={note.id}
            className="rounded-xl p-3 border"
            style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}
          >
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <p className="text-sm whitespace-pre-wrap" style={{ color: NEUTRAL_COLORS.black }}>{note.body}</p>
                <p className="text-xs mt-1.5" style={{ color: SYSTEM_COLORS.textMuted }}>
                  {note.author_name && `${note.author_name} · `}
                  {note.created_at && new Date(note.created_at).toLocaleString()}
                </p>
              </div>
              <button
                onClick={() => handleDelete(note.id)}
                className="flex-shrink-0 p-1 rounded transition-colors hover:bg-red-50"
                style={{ color: SYSTEM_COLORS.textSoft }}
              >
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── RecapPanel ────────────────────────────────────────────────────────────────

const RecapPanel = ({ eventId, recap = {}, onRefresh }) => {
  const toLines = (val) => (Array.isArray(val) ? val.join('\n') : val || '');

  const [form, setForm] = useState({
    summary:           recap.summary || '',
    actual_attendance: recap.actual_attendance ?? '',
    outcomes:          toLines(recap.outcomes),
    ideas:             toLines(recap.ideas),
    action_items:      toLines(recap.action_items),
    links:             toLines(recap.links),
    photos_url:        toLines(recap.photos_url),
  });
  const [saving, setSaving] = useState(false);

  const splitLines = (str) => str.split('\n').map(s => s.trim()).filter(Boolean);

  const handleSave = async () => {
    setSaving(true);
    try {
      await eventsService.saveRecap(eventId, {
        summary:           form.summary.trim() || undefined,
        actual_attendance: form.actual_attendance !== '' ? Number(form.actual_attendance) : undefined,
        outcomes:          splitLines(form.outcomes),
        ideas:             splitLines(form.ideas),
        action_items:      splitLines(form.action_items),
        links:             splitLines(form.links),
        photos_url:        splitLines(form.photos_url),
      });
      toast.success('Recap saved');
      onRefresh();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const set = (name) => (e) => setForm(f => ({ ...f, [name]: e.target.value }));

  return (
    <div className="space-y-4">
      <InputField label="Summary">
        <textarea rows={3} value={form.summary} onChange={set('summary')}
          placeholder="Overall event summary…"
          className="w-full rounded-xl border px-3 py-2 text-sm outline-none resize-none" style={inputStyle} />
      </InputField>
      <InputField label="Actual Attendance">
        <input type="number" value={form.actual_attendance} onChange={set('actual_attendance')}
          placeholder="0"
          className="w-full rounded-xl border px-3 py-2 text-sm outline-none" style={inputStyle} />
      </InputField>
      <InputField label="Outcomes (one per line)">
        <textarea rows={3} value={form.outcomes} onChange={set('outcomes')}
          placeholder="What was achieved…"
          className="w-full rounded-xl border px-3 py-2 text-sm outline-none resize-none" style={inputStyle} />
      </InputField>
      <InputField label="Ideas Generated (one per line)">
        <textarea rows={3} value={form.ideas} onChange={set('ideas')}
          placeholder="Ideas from the event…"
          className="w-full rounded-xl border px-3 py-2 text-sm outline-none resize-none" style={inputStyle} />
      </InputField>
      <InputField label="Action Items (one per line)">
        <textarea rows={3} value={form.action_items} onChange={set('action_items')}
          placeholder="Follow-up actions…"
          className="w-full rounded-xl border px-3 py-2 text-sm outline-none resize-none" style={inputStyle} />
      </InputField>
      <InputField label="Relevant Links (one per line)">
        <textarea rows={2} value={form.links} onChange={set('links')}
          placeholder="https://…"
          className="w-full rounded-xl border px-3 py-2 text-sm outline-none resize-none" style={inputStyle} />
      </InputField>
      <InputField label="Photo URLs (one per line)">
        <textarea rows={2} value={form.photos_url} onChange={set('photos_url')}
          placeholder="https://…"
          className="w-full rounded-xl border px-3 py-2 text-sm outline-none resize-none" style={inputStyle} />
      </InputField>
      <button
        onClick={handleSave}
        disabled={saving}
        className="flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
        style={{ backgroundColor: BRAND_COLORS.primary }}
      >
        {saving ? 'Saving…' : 'Save Recap'}
      </button>
    </div>
  );
};

// ── EventFormModal ────────────────────────────────────────────────────────────

const EventFormModal = ({ initial, onClose, onSaved }) => {
  const isEdit = !!initial?.id;

  const [form, setForm] = useState({
    title:              initial?.title              || '',
    summary:            initial?.summary            || '',
    description:        initial?.description        || '',
    event_type:         initial?.event_type         || 'training',
    mode:               initial?.mode               || 'in_person',
    status:             initial?.status             || 'planning',
    start_at:           initial?.start_at           ? initial.start_at.slice(0, 16) : '',
    end_at:             initial?.end_at             ? initial.end_at.slice(0, 16)   : '',
    timezone:           initial?.timezone           || 'Africa/Dar_es_Salaam',
    location:           initial?.location           || '',
    meeting_url:        initial?.meeting_url        || '',
    passcode:           initial?.passcode           || '',
    target_audience:    initial?.target_audience    || '',
    target_attendance:  initial?.target_attendance  ?? '',
    tags:               Array.isArray(initial?.tags) ? initial.tags.join(', ') : '',
    speakers:           Array.isArray(initial?.speakers) ? initial.speakers : [],
    agenda:             Array.isArray(initial?.agenda)   ? initial.agenda   : [],
  });
  const [saving, setSaving] = useState(false);

  const set = (name) => (e) => setForm(f => ({ ...f, [name]: e.target.value }));

  const addSpeaker = () => setForm(f => ({ ...f, speakers: [...f.speakers, { name: '', title: '', bio: '' }] }));
  const updSpeaker = (i, field, val) => setForm(f => {
    const s = [...f.speakers]; s[i] = { ...s[i], [field]: val }; return { ...f, speakers: s };
  });
  const delSpeaker = (i) => setForm(f => ({ ...f, speakers: f.speakers.filter((_, idx) => idx !== i) }));

  const addAgenda = () => setForm(f => ({ ...f, agenda: [...f.agenda, { time: '', title: '', description: '' }] }));
  const updAgenda = (i, field, val) => setForm(f => {
    const a = [...f.agenda]; a[i] = { ...a[i], [field]: val }; return { ...f, agenda: a };
  });
  const delAgenda = (i) => setForm(f => ({ ...f, agenda: f.agenda.filter((_, idx) => idx !== i) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error('Title is required');
    setSaving(true);
    try {
      const payload = {
        ...form,
        target_attendance: form.target_attendance !== '' ? Number(form.target_attendance) : undefined,
        tags:     form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        speakers: form.speakers.filter(s => s.name.trim()),
        agenda:   form.agenda.filter(a => a.title.trim()),
      };
      if (isEdit) {
        await eventsService.update(initial.id, payload);
        toast.success('Event updated');
      } else {
        await eventsService.create(payload);
        toast.success('Event created');
      }
      onSaved();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const textInput = (name, placeholder, type = 'text') => (
    <input type={type} value={form[name]} onChange={set(name)} placeholder={placeholder}
      className="w-full rounded-xl border px-3 py-2 text-sm outline-none" style={inputStyle} />
  );

  const selectInput = (name, options) => (
    <select value={form[name]} onChange={set(name)}
      className="w-full rounded-xl border px-3 py-2 text-sm outline-none" style={inputStyle}>
      {options.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
    </select>
  );

  const showLocation   = form.mode === 'in_person' || form.mode === 'hybrid';
  const showMeetingUrl = form.mode === 'virtual'   || form.mode === 'hybrid';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: SYSTEM_COLORS.shellOverlay }}>
      <div className="w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col rounded-2xl border"
        style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft, boxShadow: SYSTEM_COLORS.shadowStrong }}>

        <div className="flex items-center justify-between px-5 py-4 flex-shrink-0"
          style={{ backgroundColor: BRAND_COLORS.primary }}>
          <h2 className="text-base font-bold text-white">
            {isEdit ? 'Edit Event' : 'Create Event'}
          </h2>
          <button
            onClick={onClose}
            className="flex items-center justify-center rounded-lg transition-colors"
            style={{ width: 32, height: 32, color: 'rgba(255,255,255,0.85)' }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.12)'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <InputField label="Title" required>
            {textInput('title', 'Event title…')}
          </InputField>
          <InputField label="Summary">
            {textInput('summary', 'One-line summary…')}
          </InputField>
          <InputField label="Description">
            <textarea rows={3} value={form.description} onChange={set('description')}
              placeholder="Detailed description…"
              className="w-full rounded-xl border px-3 py-2 text-sm outline-none resize-none" style={inputStyle} />
          </InputField>

          <div className="grid grid-cols-2 gap-3">
            <InputField label="Event Type">
              {selectInput('event_type', EVENT_TYPES.map(t => ({ value: t, label: TYPE_META[t]?.label || t })))}
            </InputField>
            <InputField label="Mode">
              {selectInput('mode', EVENT_MODES.map(m => ({ value: m, label: MODE_META[m]?.label || m })))}
            </InputField>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <InputField label="Status">
              {selectInput('status', EVENT_STATUSES.map(s => ({ value: s, label: STATUS_META[s]?.label || s })))}
            </InputField>
            <InputField label="Timezone">
              {textInput('timezone', 'Africa/Dar_es_Salaam')}
            </InputField>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <InputField label="Start">
              {textInput('start_at', '', 'datetime-local')}
            </InputField>
            <InputField label="End">
              {textInput('end_at', '', 'datetime-local')}
            </InputField>
          </div>

          {showLocation && (
            <InputField label="Location">
              {textInput('location', 'Venue address or name…')}
            </InputField>
          )}
          {showMeetingUrl && (
            <div className="grid grid-cols-2 gap-3">
              <InputField label="Meeting URL">
                {textInput('meeting_url', 'https://…', 'url')}
              </InputField>
              <InputField label="Passcode">
                {textInput('passcode', 'Optional')}
              </InputField>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <InputField label="Target Audience">
              {textInput('target_audience', 'Students, engineers…')}
            </InputField>
            <InputField label="Target Attendance">
              {textInput('target_attendance', '50', 'number')}
            </InputField>
          </div>

          <InputField label="Tags (comma-separated)">
            {textInput('tags', 'orientation, udsm, annual…')}
          </InputField>

          {/* Speakers */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold" style={{ color: SYSTEM_COLORS.textMuted }}>Speakers</span>
              <button type="button" onClick={addSpeaker}
                className="flex items-center gap-1 text-xs font-semibold"
                style={{ color: BRAND_COLORS.primary }}>
                <Plus size={12} /> Add Speaker
              </button>
            </div>
            {form.speakers.map((spk, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input placeholder="Name" value={spk.name} onChange={e => updSpeaker(i, 'name', e.target.value)}
                  className="flex-1 rounded-xl border px-2 py-1.5 text-sm outline-none" style={inputStyle} />
                <input placeholder="Title/Role" value={spk.title} onChange={e => updSpeaker(i, 'title', e.target.value)}
                  className="flex-1 rounded-xl border px-2 py-1.5 text-sm outline-none" style={inputStyle} />
                <button type="button" onClick={() => delSpeaker(i)}
                  className="p-1.5 rounded-lg hover:bg-red-50 flex-shrink-0"
                  style={{ color: SYSTEM_COLORS.danger }}>
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>

          {/* Agenda */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold" style={{ color: SYSTEM_COLORS.textMuted }}>Agenda</span>
              <button type="button" onClick={addAgenda}
                className="flex items-center gap-1 text-xs font-semibold"
                style={{ color: BRAND_COLORS.primary }}>
                <Plus size={12} /> Add Item
              </button>
            </div>
            {form.agenda.map((item, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input placeholder="09:00" value={item.time} onChange={e => updAgenda(i, 'time', e.target.value)}
                  className="w-24 flex-shrink-0 rounded-xl border px-2 py-1.5 text-sm outline-none" style={inputStyle} />
                <input placeholder="Session title" value={item.title} onChange={e => updAgenda(i, 'title', e.target.value)}
                  className="flex-1 rounded-xl border px-2 py-1.5 text-sm outline-none" style={inputStyle} />
                <button type="button" onClick={() => delAgenda(i)}
                  className="p-1.5 rounded-lg hover:bg-red-50 flex-shrink-0"
                  style={{ color: SYSTEM_COLORS.danger }}>
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-3 px-5 py-4 border-t flex-shrink-0"
          style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
          <button type="button" onClick={onClose}
            className="rounded-xl px-4 py-2 text-sm font-semibold border"
            style={{ borderColor: SYSTEM_COLORS.borderSoft, color: SYSTEM_COLORS.textMuted }}>
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={saving}
            className="rounded-xl px-5 py-2 text-sm font-semibold text-white disabled:opacity-50"
            style={{ backgroundColor: BRAND_COLORS.primary }}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Event'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── DetailModal ───────────────────────────────────────────────────────────────

const DETAIL_TABS = ['overview', 'checklist', 'notes', 'recap'];

const DetailModal = ({ eventId, onClose, onListRefresh }) => {
  const [event,         setEvent]         = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [activeTab,     setActiveTab]     = useState('overview');
  const [showEdit,      setShowEdit]      = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting,      setDeleting]      = useState(false);

  const refresh = useCallback(async () => {
    try {
      const data = await eventsService.get(eventId);
      setEvent(data?.event || data);
    } catch (err) { toast.error(err.message); }
  }, [eventId]);

  useEffect(() => {
    setLoading(true);
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await eventsService.delete(eventId);
      toast.success('Event deleted');
      onListRefresh();
      onClose();
    } catch (err) {
      toast.error(err.message);
      setDeleting(false);
    }
  };

  const handleStatusChange = async (status) => {
    try {
      await eventsService.changeStatus(eventId, status);
      toast.success('Status updated');
      refresh();
    } catch (err) { toast.error(err.message); }
  };

  if (loading || !event) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ backgroundColor: SYSTEM_COLORS.shellOverlay }}>
        <div className="rounded-2xl border p-10"
          style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 mx-auto"
            style={{ borderColor: BRAND_COLORS.primary }} />
        </div>
      </div>
    );
  }

  const sm = STATUS_META[event.status] || STATUS_META.planning;
  const tm = TYPE_META[event.event_type] || { label: event.event_type, color: '#374151', bg: '#f3f4f6' };
  const mm = MODE_META[event.mode] || MODE_META.in_person;
  const ModeIcon = mm.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: SYSTEM_COLORS.shellOverlay }}>
      <div className="w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col rounded-2xl border"
        style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft, boxShadow: SYSTEM_COLORS.shadowStrong }}>

        {/* Header */}
        <div className="flex-shrink-0">
          <div
            className="flex items-start justify-between gap-3 px-5 pt-4 pb-3"
            style={{ backgroundColor: BRAND_COLORS.primary }}
          >
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap gap-1.5 mb-2">
                <span className="inline-flex rounded-full px-2 py-0.5 text-xs font-semibold"
                  style={{ backgroundColor: tm.bg, color: tm.color }}>{tm.label}</span>
                <span className="inline-flex rounded-full px-2 py-0.5 text-xs font-semibold"
                  style={{ backgroundColor: sm.bg, color: sm.color }}>{sm.label}</span>
                <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
                  style={{ backgroundColor: 'rgba(255,255,255,0.18)', color: 'rgba(255,255,255,0.85)' }}>
                  <ModeIcon size={10} />{mm.label}
                </span>
              </div>
              <h2 className="text-base font-bold leading-snug text-white">{event.title}</h2>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={() => setShowEdit(true)}
                title="Edit"
                className="flex items-center justify-center rounded-lg transition-colors"
                style={{ width: 30, height: 30, color: 'rgba(255,255,255,0.85)' }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.12)'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <Pencil size={14} />
              </button>
              <button
                onClick={onClose}
                className="flex items-center justify-center rounded-lg transition-colors"
                style={{ width: 30, height: 30, color: 'rgba(255,255,255,0.85)' }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.12)'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b" style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
            {DETAIL_TABS.map(t => (
              <button key={t} onClick={() => setActiveTab(t)}
                className="px-3 py-2.5 text-sm font-semibold capitalize whitespace-nowrap border-b-2 transition-colors"
                style={{
                  borderBottomColor: activeTab === t ? BRAND_COLORS.primary : 'transparent',
                  color: activeTab === t ? BRAND_COLORS.primary : SYSTEM_COLORS.textMuted,
                }}>
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">

          {/* Overview */}
          {activeTab === 'overview' && (
            <div className="space-y-4">
              {event.summary && (
                <p className="text-sm font-medium" style={{ color: NEUTRAL_COLORS.black }}>{event.summary}</p>
              )}
              {event.description && (
                <p className="text-sm whitespace-pre-wrap" style={{ color: SYSTEM_COLORS.textMuted }}>{event.description}</p>
              )}

              {/* Date/Time */}
              {event.start_at && (
                <div className="flex items-start gap-2.5 p-3 rounded-xl border"
                  style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                  <Calendar size={15} className="flex-shrink-0 mt-0.5" style={{ color: BRAND_COLORS.primary }} />
                  <div>
                    <p className="text-sm font-medium" style={{ color: NEUTRAL_COLORS.black }}>
                      {new Date(event.start_at).toLocaleString()}
                    </p>
                    {event.end_at && (
                      <p className="text-xs mt-0.5" style={{ color: SYSTEM_COLORS.textMuted }}>
                        to {new Date(event.end_at).toLocaleString()}
                      </p>
                    )}
                    {event.timezone && (
                      <p className="text-xs mt-0.5" style={{ color: SYSTEM_COLORS.textMuted }}>{event.timezone}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Location / Meeting URL */}
              {(event.location || event.meeting_url) && (
                <div className="flex items-start gap-2.5 p-3 rounded-xl border"
                  style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                  <MapPin size={15} className="flex-shrink-0 mt-0.5" style={{ color: BRAND_COLORS.primary }} />
                  <div className="min-w-0">
                    {event.location && (
                      <p className="text-sm" style={{ color: NEUTRAL_COLORS.black }}>{event.location}</p>
                    )}
                    {event.meeting_url && (
                      <a href={event.meeting_url} target="_blank" rel="noopener noreferrer"
                        className="text-sm break-all" style={{ color: BRAND_COLORS.primary }}>
                        {event.meeting_url}
                      </a>
                    )}
                    {event.passcode && (
                      <p className="text-xs mt-0.5" style={{ color: SYSTEM_COLORS.textMuted }}>Passcode: {event.passcode}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Audience / Attendance */}
              {(event.target_audience || event.target_attendance) && (
                <div className="flex gap-3">
                  {event.target_audience && (
                    <div className="flex-1 p-3 rounded-xl border"
                      style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                      <p className="text-xs font-semibold mb-0.5" style={{ color: SYSTEM_COLORS.textMuted }}>Target Audience</p>
                      <p className="text-sm" style={{ color: NEUTRAL_COLORS.black }}>{event.target_audience}</p>
                    </div>
                  )}
                  {event.target_attendance && (
                    <div className="p-3 rounded-xl border"
                      style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                      <p className="text-xs font-semibold mb-0.5" style={{ color: SYSTEM_COLORS.textMuted }}>Target</p>
                      <p className="text-sm font-bold" style={{ color: NEUTRAL_COLORS.black }}>{event.target_attendance}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Speakers */}
              {event.speakers?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold mb-2" style={{ color: SYSTEM_COLORS.textMuted }}>Speakers</p>
                  <div className="space-y-1.5">
                    {event.speakers.map((spk, i) => (
                      <div key={i} className="flex items-center gap-2.5 p-2.5 rounded-xl border"
                        style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                        <div className="flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold text-white flex-shrink-0"
                          style={{ backgroundColor: BRAND_COLORS.primary }}>
                          {spk.name?.charAt(0)?.toUpperCase() || 'S'}
                        </div>
                        <div>
                          <p className="text-sm font-medium" style={{ color: NEUTRAL_COLORS.black }}>{spk.name}</p>
                          {spk.title && <p className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>{spk.title}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Agenda */}
              {event.agenda?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold mb-2" style={{ color: SYSTEM_COLORS.textMuted }}>Agenda</p>
                  <div className="space-y-1.5">
                    {event.agenda.map((item, i) => (
                      <div key={i} className="flex gap-3 p-2.5 rounded-xl border"
                        style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                        {item.time && (
                          <span className="text-xs font-mono font-bold flex-shrink-0 mt-0.5"
                            style={{ color: BRAND_COLORS.primary }}>{item.time}</span>
                        )}
                        <div>
                          <p className="text-sm font-medium" style={{ color: NEUTRAL_COLORS.black }}>{item.title}</p>
                          {item.description && (
                            <p className="text-xs mt-0.5" style={{ color: SYSTEM_COLORS.textMuted }}>{item.description}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tags */}
              {event.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {event.tags.map(tag => (
                    <span key={tag} className="inline-flex rounded-full px-2.5 py-1 text-xs"
                      style={{ backgroundColor: SYSTEM_COLORS.primaryTint, color: BRAND_COLORS.primary }}>
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Status change */}
              <div className="pt-2 border-t" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                <p className="text-xs font-semibold mb-2" style={{ color: SYSTEM_COLORS.textMuted }}>Change Status</p>
                <div className="flex flex-wrap gap-1.5">
                  {EVENT_STATUSES.map(s => {
                    const m = STATUS_META[s];
                    const isActive = event.status === s;
                    return (
                      <button key={s}
                        onClick={() => !isActive && handleStatusChange(s)}
                        disabled={isActive}
                        className="rounded-full px-2.5 py-1 text-xs font-semibold transition-opacity"
                        style={{ backgroundColor: m.bg, color: m.color, opacity: isActive ? 1 : 0.6, cursor: isActive ? 'default' : 'pointer' }}>
                        {isActive && '✓ '}{m.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Delete zone */}
              <div className="pt-2 border-t" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                {!confirmDelete ? (
                  <button onClick={() => setConfirmDelete(true)} className="text-xs font-semibold"
                    style={{ color: SYSTEM_COLORS.danger }}>
                    Delete Event
                  </button>
                ) : (
                  <div className="flex items-center gap-3 p-3 rounded-xl"
                    style={{ backgroundColor: SYSTEM_COLORS.dangerTint, border: `1px solid ${SYSTEM_COLORS.danger}20` }}>
                    <p className="text-xs flex-1" style={{ color: SYSTEM_COLORS.danger }}>
                      This cannot be undone. Delete this event?
                    </p>
                    <button onClick={() => setConfirmDelete(false)} className="text-xs font-semibold"
                      style={{ color: SYSTEM_COLORS.textMuted }}>Cancel</button>
                    <button onClick={handleDelete} disabled={deleting} className="text-xs font-semibold"
                      style={{ color: SYSTEM_COLORS.danger }}>
                      {deleting ? 'Deleting…' : 'Delete'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'checklist' && (
            <ChecklistPanel eventId={eventId} items={event.checklist || []} onRefresh={refresh} />
          )}

          {activeTab === 'notes' && (
            <NotesPanel eventId={eventId} notes={event.notes || []} onRefresh={refresh} />
          )}

          {activeTab === 'recap' && (
            <RecapPanel eventId={eventId} recap={event.recap || {}} onRefresh={refresh} />
          )}
        </div>
      </div>

      {showEdit && (
        <EventFormModal
          initial={event}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); refresh(); onListRefresh(); }}
        />
      )}
    </div>
  );
};

// ── EventCard ─────────────────────────────────────────────────────────────────

const EventCard = ({ event, onOpen }) => {
  const sm = STATUS_META[event.status] || STATUS_META.planning;
  const tm = TYPE_META[event.event_type] || { label: event.event_type, color: '#374151', bg: '#f3f4f6' };
  const mm = MODE_META[event.mode] || MODE_META.in_person;
  const ModeIcon = mm.icon;
  const checklist = event.checklist || [];
  const doneCount = checklist.filter(i => i.done).length;
  const pct = checklist.length ? Math.round((doneCount / checklist.length) * 100) : 0;

  return (
    <button
      onClick={() => onOpen(event)}
      className="group relative flex flex-col w-full text-left rounded-2xl border cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
      style={{
        backgroundColor: SYSTEM_COLORS.shellSurface,
        borderColor: SYSTEM_COLORS.borderSoft,
        boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
      }}
    >
      {/* Body */}
      <div className="flex-1 p-5 space-y-3">
        <div className="flex flex-wrap items-start gap-1.5">
          <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold"
            style={{ backgroundColor: tm.bg, color: tm.color }}>
            <span className="h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: tm.color }} />
            {tm.label}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold"
            style={{ backgroundColor: sm.bg, color: sm.color }}>
            <span className="h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: sm.color }} />
            {sm.label}
          </span>
          <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ml-auto flex-shrink-0"
            style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted, color: SYSTEM_COLORS.textMuted }}>
            <ModeIcon size={10} />{mm.label}
          </span>
        </div>

        <h3 className="text-sm font-bold leading-snug line-clamp-2" style={{ color: NEUTRAL_COLORS.black }}>
          {event.title}
        </h3>

        {event.summary && (
          <p className="text-xs line-clamp-2" style={{ color: SYSTEM_COLORS.textMuted }}>{event.summary}</p>
        )}

        {checklist.length > 0 && (
          <div className="rounded-lg px-3 py-2.5" style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
            <div className="flex justify-between text-xs mb-1.5" style={{ color: SYSTEM_COLORS.textMuted }}>
              <span>Checklist</span>
              <span className="font-semibold">{doneCount}/{checklist.length} · {pct}%</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full" style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }}>
              <div className="h-1.5 rounded-full transition-all"
                style={{ width: `${pct}%`, backgroundColor: SYSTEM_COLORS.success }} />
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between border-t px-5 py-3"
           style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
        {event.start_at ? (
          <div className="flex items-center gap-1.5">
            <Calendar size={12} style={{ color: SYSTEM_COLORS.textSoft, flexShrink: 0 }} />
            <span className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>
              {new Date(event.start_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
          </div>
        ) : (
          <span />
        )}
        {event.target_attendance && (
          <div className="flex items-center gap-1">
            <Users size={11} style={{ color: SYSTEM_COLORS.textSoft }} />
            <span className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>{event.target_attendance}</span>
          </div>
        )}
      </div>
    </button>
  );
};

// ── EventsPage ────────────────────────────────────────────────────────────────

const EventsPage = () => {
  const [events,       setEvents]       = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [typeFilter,   setTypeFilter]   = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [modeFilter,   setModeFilter]   = useState('');
  const [fromDate,     setFromDate]     = useState('');
  const [toDate,       setToDate]       = useState('');
  const [showCreate,   setShowCreate]   = useState(false);
  const [selectedId,   setSelectedId]   = useState(null);
  const [searchQuery,  setSearchQuery]  = useState('');

  const fetchEvents = useCallback(async () => {
    try {
      const data = await eventsService.list({
        eventType:  typeFilter   || undefined,
        status:     statusFilter || undefined,
        mode:       modeFilter   || undefined,
        from:       fromDate     || undefined,
        to:         toDate       || undefined,
      });
      setEvents(Array.isArray(data) ? data : data?.events || data?.items || []);
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  }, [typeFilter, statusFilter, modeFilter, fromDate, toDate]);

  useEffect(() => {
    setLoading(true);
    fetchEvents();
  }, [fetchEvents]);

  const upcoming  = events.filter(e => e.status === 'scheduled').length;
  const ongoing   = events.filter(e => e.status === 'ongoing').length;
  const completed = events.filter(e => e.status === 'completed').length;

  const hasFilters = typeFilter || statusFilter || modeFilter || fromDate || toDate || searchQuery;
  const clearFilters = () => {
    setTypeFilter(''); setStatusFilter(''); setModeFilter('');
    setFromDate(''); setToDate(''); setSearchQuery('');
  };

  const filteredEvents = searchQuery
    ? events.filter(e =>
        e.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.summary?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : events;

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: BRAND_COLORS.primary }}>
            Activities
          </p>
          <h1 className="mt-0.5 text-2xl font-bold" style={{ color: NEUTRAL_COLORS.black }}>Events</h1>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white flex-shrink-0"
          style={{ backgroundColor: BRAND_COLORS.primary }}
        >
          <Plus size={15} /> Create Event
        </button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Events', value: events.length, icon: CalendarRange, tint: SYSTEM_COLORS.primaryTint,  color: BRAND_COLORS.primary    },
          { label: 'Scheduled',    value: upcoming,      icon: Calendar,      tint: SYSTEM_COLORS.infoTint,     color: SYSTEM_COLORS.info      },
          { label: 'Ongoing',      value: ongoing,       icon: Activity,      tint: SYSTEM_COLORS.successTint,  color: SYSTEM_COLORS.success   },
          { label: 'Completed',    value: completed,     icon: CheckCircle2,  tint: SYSTEM_COLORS.surfaceMuted, color: SYSTEM_COLORS.textMuted },
        ].map(({ label, value, icon: Icon, tint, color }) => (
          <div key={label} className="flex items-center gap-3 px-4 py-4 rounded-xl border"
               style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
            <div className="flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0"
                 style={{ backgroundColor: tint }}>
              <Icon size={18} style={{ color }} />
            </div>
            <div>
              <p className="text-xl font-bold leading-none" style={{ color }}>{value}</p>
              <p className="text-xs mt-0.5" style={{ color: SYSTEM_COLORS.textMuted }}>{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 rounded-xl border px-2 py-2 shadow-sm"
           style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
        <div className="flex flex-1 min-w-[160px] items-center gap-2 rounded-lg border px-3 py-1.5"
             style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
          <Search size={14} style={{ color: SYSTEM_COLORS.textSoft, flexShrink: 0 }} />
          <input
            type="text"
            placeholder="Search events…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="flex-1 text-sm outline-none bg-transparent min-w-0"
            style={{ color: NEUTRAL_COLORS.black }}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')}>
              <X size={12} style={{ color: SYSTEM_COLORS.textSoft }} />
            </button>
          )}
        </div>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
          className="rounded-lg border px-2.5 py-1.5 text-sm outline-none"
          style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface, color: typeFilter ? NEUTRAL_COLORS.black : SYSTEM_COLORS.textMuted }}>
          <option value="">All Types</option>
          {EVENT_TYPES.map(t => <option key={t} value={t}>{TYPE_META[t].label}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="rounded-lg border px-2.5 py-1.5 text-sm outline-none"
          style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface, color: statusFilter ? NEUTRAL_COLORS.black : SYSTEM_COLORS.textMuted }}>
          <option value="">All Statuses</option>
          {EVENT_STATUSES.map(s => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
        </select>
        <select value={modeFilter} onChange={e => setModeFilter(e.target.value)}
          className="rounded-lg border px-2.5 py-1.5 text-sm outline-none"
          style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface, color: modeFilter ? NEUTRAL_COLORS.black : SYSTEM_COLORS.textMuted }}>
          <option value="">All Modes</option>
          {EVENT_MODES.map(m => <option key={m} value={m}>{MODE_META[m].label}</option>)}
        </select>
        <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
          className="rounded-lg border px-2.5 py-1.5 text-sm outline-none"
          style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface, color: fromDate ? NEUTRAL_COLORS.black : SYSTEM_COLORS.textMuted }} />
        <span className="text-xs flex-shrink-0" style={{ color: SYSTEM_COLORS.textMuted }}>to</span>
        <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
          className="rounded-lg border px-2.5 py-1.5 text-sm outline-none"
          style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface, color: toDate ? NEUTRAL_COLORS.black : SYSTEM_COLORS.textMuted }} />
        {hasFilters && (
          <button onClick={clearFilters}
            className="text-xs font-semibold flex-shrink-0 px-2 py-1.5 rounded-lg transition-colors hover:bg-red-50"
            style={{ color: SYSTEM_COLORS.danger }}>
            Clear
          </button>
        )}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i}
              className="rounded-2xl border p-4 space-y-3 animate-pulse"
              style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
              <div className="flex gap-2">
                <div className="h-5 w-20 rounded-full" style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }} />
                <div className="h-5 w-16 rounded-full" style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }} />
              </div>
              <div className="h-4 w-3/4 rounded" style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }} />
              <div className="h-3 w-full rounded" style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }} />
              <div className="h-3 w-1/2 rounded" style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }} />
            </div>
          ))}
        </div>
      ) : filteredEvents.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 py-20 rounded-2xl border"
          style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
          <div className="flex items-center justify-center w-12 h-12 rounded-2xl"
            style={{ backgroundColor: SYSTEM_COLORS.primaryTint }}>
            <CalendarRange size={22} style={{ color: BRAND_COLORS.primary }} />
          </div>
          <p className="text-sm font-semibold" style={{ color: NEUTRAL_COLORS.black }}>No events found</p>
          <p className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>
            {hasFilters ? 'Try adjusting your filters' : 'Create your first event to get started'}
          </p>
          {!hasFilters && (
            <button onClick={() => setShowCreate(true)}
              className="mt-1 inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white"
              style={{ backgroundColor: BRAND_COLORS.primary }}>
              <Plus size={14} /> Create Event
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredEvents.map(event => (
            <EventCard key={event.id} event={event} onOpen={(e) => setSelectedId(e.id)} />
          ))}
        </div>
      )}

      {showCreate && (
        <EventFormModal
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); fetchEvents(); }}
        />
      )}

      {selectedId && (
        <DetailModal
          eventId={selectedId}
          onClose={() => setSelectedId(null)}
          onListRefresh={fetchEvents}
        />
      )}
    </div>
  );
};

export default EventsPage;
